import React, { useEffect } from 'react';
import { Badge } from '../lib/badge';
import Icon from '../components/icons';
import { markdownToHtml } from '../lib/simpleMarkdown';

/**
 * Shared Claude skill-output modal (Tasks + Runs).
 */
export default function ClaudeOutputModal({
  title,
  run,
  runs,
  selectedIdx = 0,
  onSelectRun,
  loading,
  err,
  onClose,
}) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const list = runs || (run ? [run] : []);
  const active = list[selectedIdx] || run || null;
  const html = active?.output ? markdownToHtml(active.output) : '';

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal-panel modal-panel--output" onClick={(e) => e.stopPropagation()}>
        <div className="modal-panel__header">
          <div className="output-modal__title-block">
            <p className="output-modal__eyebrow">Claude skill result</p>
            <h3>{title}</h3>
          </div>
          <button type="button" className="modal-panel__close" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>

        <div className="modal-panel__body output-modal__body">
          {loading && <p className="status status--muted">Loading runs…</p>}
          {err && <div className="status status--error">{err}</div>}
          {!loading && !err && list.length === 0 && (
            <p className="status status--muted">No orchestration runs yet.</p>
          )}

          {!loading && list.length > 1 && onSelectRun && (
            <div className="run-picker">
              {list.map((r, i) => (
                <button
                  key={r.id}
                  type="button"
                  className={`filter-chip ${i === selectedIdx ? 'filter-chip--active' : ''}`}
                  onClick={() => onSelectRun(i)}
                >
                  Run #{r.id} · {r.status}
                </button>
              ))}
            </div>
          )}

          {active && (
            <div className="claude-output">
              <div className="claude-output__meta">
                {active.skill_key && (
                  <span className="output-chip">{active.skill_key}</span>
                )}
                <Badge value={active.status} />
                {active.model && <span className="output-chip output-chip--muted">{active.model}</span>}
                {active.credits_used != null && (
                  <span className="output-chip output-chip--muted">{active.credits_used} credits</span>
                )}
                {active.workspace_name && (
                  <span className="output-chip output-chip--muted">{active.workspace_name}</span>
                )}
                {active.created_at && (
                  <span className="output-chip output-chip--muted">
                    {new Date(active.created_at).toLocaleString()}
                  </span>
                )}
              </div>

              {active.error && (
                <div className="status status--error">
                  {active.error}
                  {active.reason ? ` — ${active.reason}` : ''}
                </div>
              )}

              {html ? (
                <div className="md-prose" dangerouslySetInnerHTML={{ __html: html }} />
              ) : !active.error ? (
                <p className="status status--muted">No Claude text stored for this run yet.</p>
              ) : null}

              {active.usage && (
                <p className="claude-output__usage">
                  Tokens — in {active.usage.input_tokens ?? '—'} · out {active.usage.output_tokens ?? '—'}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="modal-panel__footer">
          <button type="button" className="btn-secondary btn-small" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
